// Phase G4-A — attemptLogger: persist + read ChildAttempt rows.
//
// Writes to the child_attempts table (migration 0005) via pencilwise's
// REAL supabase client (src/supabaseClient.js) — NOT the G3 no-op mock
// at @/integrations/supabase/client. The mock swallows writes; using
// it here would silently drop every attempt.
//
// The real client is untyped JS (no Database generic, no
// supabase/types.ts), so reads are cast to ChildAttempt[] after the
// snake_case → camelCase remap below.
//
// Persistence policy (per G4 plan §G4-Q6 resolution): direct write +
// in-session retry buffer (3 attempts, linear backoff). NO IndexedDB
// offline queue — deferred to post-launch. On permanent failure the
// attempt is dropped after retries and the error surfaced to the
// caller (session screen shows a toast); the engine still works on
// whatever did persist.

// supabaseClient.js is untyped JS (allowJs resolves it; reads are
// cast via fromRow below). The client is a lazy Proxy in supabaseClient.js
// so importing it here does NOT trigger the WebSocket connection.
import { supabase } from "@/supabaseClient.js";
import { captureSupabaseError } from "@/lib/sentry";
import { isCircuitOpen, recordSuccess, recordFailure } from "./networkCircuitBreaker";
import type { ChildAttempt } from "@/adaptation/types";
import { appendLocalAttempt, mergeAttempts } from "./localAttemptStore";
import { fromRow } from "./attemptRow";
import { qk } from "./queryKeys";
// MA-288 classroom-roster-identity-write — the kiosk claimed-row seam.
// kioskIdentity.ts lives under src/lib/teacher/ (not lib/engine); this
// cross-boundary import is deliberate, not a layering slip — the RPC route
// decision needs the SAME registry read persistenceIdentity.js's kiosk
// bypass depends on (see that file's own comment), and the brief's own
// "keep ONE reader" instruction means that logic belongs in exactly one
// place. Verified cycle-free: kioskIdentity.ts's own childrenStore.js
// import does not, directly or transitively, import this file.
import { isMissingFunctionError } from "@/lib/teacher/claimApi";
import { resolveKioskAttemptRoute } from "@/lib/teacher/kioskIdentity";

const TABLE = "child_attempts";
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 150;

// ── Idempotency key (MA-14) ──────────────────────────────────────────

/** Deterministic idempotency key for a child_attempts row.
 *  Format: childId:runId:itemId:retryIndex
 *  Same retry = same key; different run or item or retry slot = different key. */
function attemptEventKey(a: ChildAttempt): string {
  return `${a.childId}:${a.runId}:${a.itemId}:${a.retryIndex ?? 0}`;
}

// ── camelCase ChildAttempt → snake_case row ──────────────────────────

function toRow(a: ChildAttempt): Record<string, unknown> {
  return {
    child_id: a.childId,
    session_id: a.sessionId,
    run_id: a.runId,
    timestamp: a.timestamp,
    age_band: a.ageBand,
    domain: a.domain,
    skill_id: a.skillId,
    item_id: a.itemId,
    item_type: a.itemType,
    correct: a.correct,
    selected_answer: a.selectedAnswer == null ? null : String(a.selectedAnswer),
    expected_answer: String(a.expectedAnswer),
    response_channel: a.responseChannel,
    support_mode: a.supportMode,
    hint_level: a.hintLevel,
    scaffold_used: a.scaffoldUsed,
    read_aloud_used: a.readAloudUsed,
    picture_support_used: a.pictureSupportUsed,
    adult_support_used: a.adultSupportUsed,
    mascot_support_used: a.mascotSupportUsed,
    handwriting_required: a.handwritingRequired,
    handwriting_recognized: a.handwritingRecognized,
    handwriting_confidence: a.handwritingConfidence,
    numeral_formation_issue: a.numeralFormationIssue,
    oral_response: a.oralResponse,
    latency_ms: a.latencyMs,
    error_type: a.errorType,
    misconception_id: a.misconceptionId,
    is_transfer_task: a.isTransferTask,
    transfer_task_id: a.transferTaskId,
    transfer_type: a.transferType,
    is_delayed_review: a.isDelayedReview,
    content_launch_status: a.contentLaunchStatus,
    expert_review_needed: a.expertReviewNeeded,
    content_file: a.contentFile,
    generator_version: a.generatorVersion,
    validator_version: a.validatorVersion,
    curriculum_version: a.curriculumVersion,
    content_hash: a.contentHash,
    raw_child_facing_llm: a.rawChildFacingLLM,
    frustration_signal: a.frustrationSignal ?? null,
    confidence_band: a.confidenceBand ?? null,
    // ── P-METADATA-EXPAND-A bedrock + TA-1 FIX ───────────────────────
    // session_source: TA-1 finding. The field has been on the TS type
    // since T1.4b but toRow omitted it — every attempt persisted with
    // the column NULL (or, post-migration-0006, the table default
    // 'path'). Now serialized explicitly with the same default so
    // filterPathAttempts behaviour is unchanged for the common path.
    session_source: a.sessionSource ?? "path",
    subject: a.subject ?? null,
    target_grade_band: a.targetGradeBand ?? null,
    adaptive_reason: a.adaptiveReason ?? null,
    last_success_at: a.lastSuccessAt ?? null,
    // P-TIER3-INSTRUMENTATION-A — derived scaffold-family label (0008).
    scaffold_type_used: a.scaffoldTypeUsed ?? null,
    // P-TIER3-INSTRUMENTATION-B — per-item write-time fields (0009).
    // WIRED four default to the column DEFAULT when absent; the three STUB
    // columns serialize their no-producer defaults (0 / 0 / null).
    hint_count: a.hintCount ?? 0,
    audio_replay_count: a.audioReplayCount ?? 0,
    prompt_repeat_count: a.promptRepeatCount ?? 0,
    retry_index: a.retryIndex ?? 0,
    is_repetition: a.isRepetition ?? false,
    mastery_before: a.masteryBefore ?? null,
    difficulty_tier: a.difficultyTier ?? 1,
    mastery_after: a.masteryAfter ?? null,
    // ── PR-CANONICAL-EVIDENCE-A2 — canonical evidence columns (0010). ──
    // audience_mode is screen-supplied on every fresh write; evidence_contract
    // _version is stamped by buildAttempt. ?? null only guards a hand-built
    // ChildAttempt that omits them (legacy-shaped); fresh writes always carry
    // both. NB ?? null on read is undefined→null here (DB stores null), but
    // fromRow re-reads null→undefined so the legacy/version-filter semantic
    // survives the round trip.
    audience_mode: a.audienceMode ?? null,
    evidence_contract_version: a.evidenceContractVersion ?? null,
    // ── PR-CANONICAL-EVIDENCE-A3.1 — answer_revealed column (0011). ─────
    // buildAttempt defaults a fresh write to false; ?? null only guards a
    // hand-built ChildAttempt that omits the field. fromRow re-reads
    // null→undefined so the legacy-row semantic survives the round trip.
    answer_revealed: a.answerRevealed ?? null,
    // ── MA-14: idempotency key (migration 0015). ─────────────────────
    // Derived deterministically so a retry of the same logical attempt
    // produces the same key → ON CONFLICT DO NOTHING on the server.
    event_key: attemptEventKey(a),
  };
}

// ── snake_case row → camelCase ChildAttempt (attemptRow.ts, MA-874 D-1101-23) ──

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

/** The claimed-row write path: migration 0042's `insert_kiosk_child_attempt`
 *  SECURITY DEFINER RPC. Same retry/circuit-breaker shape as the plain
 *  upsert loop below (linear backoff, MAX_RETRIES) — the RPC overrides
 *  `child_id`/`session_id` server-side from `p_enrollment_id` regardless of
 *  what `row` carries for them, so passing the already-toRow()'d row as-is
 *  is safe (never trusted, just convenient shape reuse).
 *
 *  isMissingFunctionError → an HONEST, SILENT drop: `return false` with NO
 *  `recordFailure()` call. This is a deliberate asymmetry from the normal
 *  upsert's failure branch: pre-founder-apply (migration 0042 not live),
 *  EVERY kiosk-claimed attempt would hit this exact error on EVERY call,
 *  and recordFailure()'s circuit breaker is sized for transient network
 *  failures, not a permanent pre-apply state — tripping it here would also
 *  degrade the UNRELATED plain-upsert path (the breaker is global) and/or
 *  fire a red toast storm for a condition the kiosk UI already handles
 *  gracefully (ensureKioskChildForEntry's own null-path note). Every OTHER
 *  error still records failure/captures, unchanged from the normal path. */
async function logKioskClaimedAttempt(
  enrollmentId: string,
  row: Record<string, unknown>,
  attempt: ChildAttempt,
): Promise<boolean> {
  for (let i = 0; i <= MAX_RETRIES; i++) {
    try {
      const { error } = await supabase.rpc("insert_kiosk_child_attempt", {
        p_enrollment_id: enrollmentId,
        p_row: row,
      });
      if (!error) {
        recordSuccess();
        return true;
      }
      if (isMissingFunctionError(error)) return false;
      if (i === MAX_RETRIES) {
        recordFailure();
        captureSupabaseError(`insert_kiosk_child_attempt failed after ${MAX_RETRIES} retries`, error, {
          childId: attempt.childId,
          runId: attempt.runId,
          skillId: attempt.skillId,
          enrollmentId,
        });
        return false;
      }
    } catch (e) {
      if (i === MAX_RETRIES) {
        recordFailure();
        captureSupabaseError(`insert_kiosk_child_attempt threw after ${MAX_RETRIES} retries`, e, {
          childId: attempt.childId,
          runId: attempt.runId,
          skillId: attempt.skillId,
          enrollmentId,
        });
        return false;
      }
    }
    await sleep(RETRY_BASE_MS * (i + 1));
  }
  return false;
}

/** Persist one attempt. Retries transient failures up to MAX_RETRIES
 * with linear backoff. Returns true on success, false if all retries
 * failed (caller surfaces a toast; engine still runs on persisted
 * attempts). */
export async function logAttempt(attempt: ChildAttempt): Promise<boolean> {
  // P0: skip the network while the breaker is open — the local-first
  // mirror already holds this row; stop hammering a dead backend.
  if (isCircuitOpen()) return false;
  const row = toRow(attempt);

  // MA-288 classroom-roster-identity-write — THE routing seam. By the time
  // logAttempt runs, `attempt.childId` has already been resolved to the
  // CLOUD student id (persistenceIdentity.js's resolveStudentIdAction
  // resolves a kiosk-CLAIMED active child straight to its
  // kioskClaimedStudentId, bypassing lazy-create — see that file's own
  // comment). resolveKioskAttemptRoute re-reads the SAME active registry
  // entry and, ONLY when it is kiosk-claimed AND its resolved id matches
  // this childId, returns the narrow RPC route: a plain upsert here would
  // fail child_attempts_insert's `parent_id = auth.uid()` check outright,
  // since the signed-in caller on a kiosk device is the TEACHER, not the
  // enrollment's owning parent. Every other caller (a family kid, or a
  // kiosk SHADOW student whose parent_id IS the signed-in teacher) gets
  // null here and falls through to the existing upsert branch UNTOUCHED —
  // this seam is additive and dead when no kiosk fields exist anywhere in
  // the local registry.
  const kioskRoute = resolveKioskAttemptRoute(attempt.childId);
  if (kioskRoute) {
    return logKioskClaimedAttempt(kioskRoute.enrollmentId, row, attempt);
  }

  for (let i = 0; i <= MAX_RETRIES; i++) {
    try {
      // MA-14: upsert with DO NOTHING on event_key conflict so a retried
      // write that already landed server-side is a safe no-op.
      const { error } = await supabase.from(TABLE).upsert(row, {
        onConflict: "event_key",
        ignoreDuplicates: true,
      });
      if (!error) { recordSuccess(); return true; }
      if (i === MAX_RETRIES) {
        recordFailure();
        // BUG 2 was exactly this branch silently dropping every attempt.
        captureSupabaseError(`child_attempts upsert failed after ${MAX_RETRIES} retries`, error, {
          childId: attempt.childId,
          runId: attempt.runId,
          skillId: attempt.skillId,
        });
        return false;
      }
    } catch (e) {
      if (i === MAX_RETRIES) {
        recordFailure();
        captureSupabaseError(`child_attempts upsert threw after ${MAX_RETRIES} retries`, e, {
          childId: attempt.childId,
          runId: attempt.runId,
          skillId: attempt.skillId,
        });
        return false;
      }
    }
    await sleep(RETRY_BASE_MS * (i + 1));
  }
  return false;
}

/**
 * ENGINE-LOCAL-FIRST write path. Persists one attempt LOCAL-FIRST so the
 * adaptation engine sees real history offline, then mirrors it to the cloud
 * additively when a real auth session + canonical id are available.
 *
 * This replaces the bare `void logAttempt(attempt)` call in the session
 * screens. The screens previously early-returned before logging whenever
 * `persistEnabled` was false (dev/e2e/offline) or the canonical id was
 * unresolved (offline / not-yet-linked) — discarding EVERY attempt and
 * starving the engine. Now the local mirror always lands (keyed by the LOCAL
 * child id), and the cloud write stays exactly as gated as before.
 *
 *  - localChildId: app.activeChildId (the id the local progress store +
 *    every local engine read use). Null only in degenerate states; the
 *    local write no-ops then.
 *  - persistEnabled / ensureActiveStudentId: the unchanged cloud gate. The
 *    cloud row is re-stamped with the resolved CANONICAL id so FK/RLS still
 *    hold; the local mirror keeps whatever childId the screen built.
 */
export async function persistAttemptLocalFirst(
  attempt: ChildAttempt,
  opts: {
    localChildId: string | null | undefined;
    persistEnabled: boolean;
    ensureActiveStudentId?: (() => Promise<string | null>) | undefined;
    onCloudFailure?: () => void;
    /** Test-only injection seam (mirrors outboxDrain pattern). */
    _logAttempt?: (a: ChildAttempt) => Promise<boolean>;
  },
): Promise<void> {
  // Local-first: always mirror (the offline engine reads this).
  if (opts.localChildId) appendLocalAttempt(opts.localChildId, attempt);
  // Cloud (additive): only with a real auth session + a resolvable canonical
  // students.id. Identical gate + id resolution to the pre-local-first code.
  if (!opts.persistEnabled || !opts.ensureActiveStudentId) return;
  const resolvedChildId = await opts.ensureActiveStudentId();
  if (!resolvedChildId) return;
  const logFn = opts._logAttempt ?? logAttempt;
  const ok = await logFn({ ...attempt, childId: resolvedChildId });
  if (!ok) opts.onCloudFailure?.();
}

/** Fetch recent attempts for a (child, skill), most-recent first.
 * Used by buildDecisionInput to assemble the AttemptWindow. */
export async function fetchRecentAttempts(
  childId: string,
  skillId: string,
  limit = 50,
  // MA-303b RED round 2 (S2/S3/S6) — injectable client, same pattern as
  // fetchTransferAttemptsForSkillOrThrow/fetchAllAttemptsForChildOrThrow,
  // added so buildDecisionInput's REAL wiring (not a re-implementation of
  // it) is unit-testable with a stub data client instead of live Supabase.
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<ChildAttempt[]> {
  const { data, error } = await client
    .from(TABLE)
    .select("*")
    .eq("child_id", childId)
    .eq("skill_id", skillId)
    .order("timestamp", { ascending: false })
    .limit(limit);
  if (error) {
    console.error("[attemptLogger] fetchRecentAttempts failed:", error);
    return [];
  }
  // Engine expects chronological order (oldest → newest) for windowing.
  return (data ?? []).map(fromRow).reverse();
}

/** MA-303b (RED round 1, R1; corrected round 2, S1) — the tag-filtered
 *  fetch that makes D2's "required binds only once served" contract
 *  survive real fetch windows. `is_transfer_task` is a real boolean COLUMN
 *  on `child_attempts` (migration 0005; see `toRow`/`fromRow` above) —
 *  this is an ordinary `.eq()` filter over the EXISTING data layer, no
 *  schema change, no new index or RPC required.
 *
 *  WHY THIS EXISTS: both real read paths windowed the attempt array before
 *  `evaluateTransferEvidence` ever saw it — `fetchRecentAttempts` (engine
 *  path, limit 50 for this skill) and `fetchAllAttemptsForChildOrThrow`
 *  (tree path, limit 500 ACROSS ALL SKILLS). A tagged transfer attempt is
 *  rare (at most ~1 per session) and can scroll out of either window after
 *  ordinary practice, silently flipping `required` back to `false` with no
 *  pass ever recorded — the exact "regains Mastered by waiting" defect RED
 *  found. Tagged transfer attempts are RARE by construction, so fetching a
 *  generously-capped set of them (not literally unbounded — S1(c), RED
 *  round 2: the round-1 doc comments here and at the two call sites, and
 *  the PR body, all said "unbounded" while the signature said `limit =
 *  200`; the cap is now explicit and raised to 2000, still far beyond any
 *  realistic per-skill tagged-attempt count) is cheap and correct.
 *
 *  TWO FUNCTIONS, matching the `fetchAllAttemptsForChildOrThrow` /
 *  `fetchAllAttemptsForChild` convention already in this file (S1(a), RED
 *  round 2): the `OrThrow` variant lets a caller distinguish "fetched,
 *  zero tagged rows" from "the fetch itself failed" — the round-1 version
 *  swallowed both to `[]`, so a transient RLS/network error silently
 *  reopened the exact "regains Mastered by waiting" defect this fetch
 *  exists to close, with nothing but a console.error. Both call sites
 *  (`buildDecisionInput`, engine path; `useSkillGraph`, tree path) now use
 *  `OrThrow` and, on catch, discard the WHOLE evidence set they were
 *  assembling — mirroring the sibling attempts-fetch's own existing
 *  fail-to-`[]` contract on that same call path, rather than silently
 *  keeping only the bounded half of the evidence (which is what produced
 *  the false "Mastered" chip in the first place). The plain
 *  `fetchTransferAttemptsForSkill` wrapper is kept for any caller that
 *  wants the old swallow-to-`[]` behavior. */
export async function fetchTransferAttemptsForSkillOrThrow(
  childId: string,
  skillId: string,
  limit = 2000,
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<ChildAttempt[]> {
  // MA-303b RED round 2 (NIT 1) — symmetric with evaluateTransferEvidence's
  // own predicate (`isTransferTask || transferTaskId !== null`) and with
  // loadLocalTaggedTransferAttempts' local filter. Round 1 filtered
  // `is_transfer_task` only, so a row carrying ONLY `transfer_task_id`
  // (today's producer always sets both, so this was latent, not live)
  // would have been invisible to this fetch while still counting on the
  // evidence side.
  const { data, error } = await client
    .from(TABLE)
    .select("*")
    .eq("child_id", childId)
    .eq("skill_id", skillId)
    .or("is_transfer_task.eq.true,transfer_task_id.not.is.null")
    .order("timestamp", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map(fromRow).reverse();
}

/** Swallowing wrapper — see `fetchTransferAttemptsForSkillOrThrow`'s doc
 *  comment for why the two production call sites (`buildDecisionInput`,
 *  `useSkillGraph`) use the `OrThrow` form instead of this one. */
export async function fetchTransferAttemptsForSkill(
  childId: string,
  skillId: string,
  limit = 2000,
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<ChildAttempt[]> {
  try {
    return await fetchTransferAttemptsForSkillOrThrow(childId, skillId, limit, client);
  } catch (error) {
    console.error("[attemptLogger] fetchTransferAttemptsForSkill failed:", error);
    return [];
  }
}

/** Fetch the single most recent attempt for a child, across all skills.
 * MA-61 monitor slice (premise updated by MA-294):
 * `fetchAllAttemptsForChildOrThrow` used to return the OLDEST 500 rows past
 * 500 lifetime attempts, so its last element was not reliably the most
 * recent attempt. MA-294 fixed that (it now windows to the NEWEST rows),
 * but this stays the right read for "last active": limit(1) descending is
 * correct at any scale and far cheaper than fetching a full history just
 * to read one row. Returns null on error
 * or no attempts (never-practiced student) — swallowing, like the roster's
 * other per-row status reads (e.g. claim-code cell), since a transient miss
 * here degrades to "no status shown," not a broken screen. */
export async function fetchMostRecentAttemptForChild(
  childId: string,
): Promise<ChildAttempt | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("child_id", childId)
    .order("timestamp", { ascending: false })
    .limit(1);
  if (error) {
    console.error("[attemptLogger] fetchMostRecentAttemptForChild failed:", error);
    return null;
  }
  const row = (data ?? [])[0];
  return row ? fromRow(row) : null;
}

/** Fetch ALL attempts for a child (across skills), chronological.
 * Used by useSkillGraph to compute per-skill mastery in one query
 * instead of N per-skill round trips. */
/** Throwing core: surfaces a DB error to the caller instead of swallowing
 *  it to []. Readers that must distinguish "no rows yet" from "the fetch
 *  failed" (e.g. the parent dashboard, which shows a retry surface on error
 *  rather than a false "no activity" empty state) use this.
 *
 *  MA-294 (NEWEST-history fix): this used to order ASCENDING + limit(500),
 *  which silently returns the OLDEST 500 rows once a child exceeds 500
 *  lifetime attempts (~11 days at the age-6 full dose) — every NEW attempt,
 *  including the cross-day evidence retention/confirm logic depends on,
 *  stopped entering the aggregate while the bands froze on stale history.
 *  Now it fetches DESCENDING (newest `limit` rows) and reverses back to the
 *  chronological order every consumer expects — the exact contract
 *  `fetchRecentAttempts` already uses. `event_key` (deterministic per
 *  attempt) is the secondary sort key so equal-timestamp rows order
 *  stably across fetches for post-0015 rows; legacy pre-0015 rows have a
 *  NULL event_key (Postgres DESC defaults to NULLS FIRST with no order
 *  among the NULLs), so legacy equal-timestamp ties stay as
 *  non-deterministic as they always were — the old ascending fetch had no
 *  tiebreak at all, and the aggregator re-sorts, so no consumer depends on
 *  tie order. Memory bound unchanged: at most `limit` rows are ever held.
 *
 *  `client` is a test-only injection seam (mirrors the
 *  `persistAttemptLocalFirst._logAttempt` / outboxDrain stub-client
 *  pattern) so the truncation bite can prove windowing semantics without a
 *  live Supabase. Production callers never pass it. */
export async function fetchAllAttemptsForChildOrThrow(
  childId: string,
  limit = 500,
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<ChildAttempt[]> {
  const { data, error } = await client
    .from(TABLE)
    .select("*")
    .eq("child_id", childId)
    .order("timestamp", { ascending: false })
    .order("event_key", { ascending: false })
    .limit(limit);
  if (error) throw error;
  // Consumers expect chronological (oldest → newest) for windowing —
  // same reverse contract as fetchRecentAttempts.
  return (data ?? []).map(fromRow).reverse();
}

/** Swallowing wrapper: returns [] on error (kid-facing trail/skill-graph
 *  reads tolerate a transient miss and re-fetch). Engine read sites that
 *  predate the parent dashboard keep this behavior unchanged. */
export async function fetchAllAttemptsForChild(
  childId: string,
  limit = 500,
  // MA-303b RED round 2 (S2/S3/S6) — forward the test-only client seam
  // (previously dropped by this swallowing wrapper), so buildSkillGraphNodes
  // can be exercised end-to-end (cloud AND tagged fetch) against one stub.
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<ChildAttempt[]> {
  try {
    return await fetchAllAttemptsForChildOrThrow(childId, limit, client);
  } catch (error) {
    console.error("[attemptLogger] fetchAllAttemptsForChild failed:", error);
    return [];
  }
}

/** MA-1202 (D-1103-10, RED r1 MUST-1) — rows ASKED for per page of the
 *  child's full-history read: PostgREST's default max-rows (1,000). The read
 *  never assumes the server returns that many: it advances by the rows a page
 *  actually returned and stops only when it holds the exact row count the
 *  first page reported (or, when no count came back, on an empty page). So a
 *  server whose max-rows is lower (500, say) costs more pages, never rows. */
export const FULL_HISTORY_PAGE = 1000;

/** MA-1202 (D-1103-10) — the newest-N discovery read's size. A shorter answer
 *  IS the child's whole history. */
export const HISTORY_DISCOVERY_WINDOW = 500;

/** MA-1202 — the child's COMPLETE attempt history, chronological, in pages
 *  (`timestamp asc, event_key asc, id asc` — a total order, so an OFFSET page
 *  edge can never reorder equal-timestamp legacy rows and drop one). Page 1
 *  asks for the exact row count; the read goes on until it holds that many
 *  rows (or a page comes back empty). A row inserted during the read is either
 *  a new one (it lands after the last page, or in the discovery window the
 *  caller merges) or an outbox drain carrying its device's earlier timestamp
 *  (it lands mid-history and can shift a later page by one, so a row can be
 *  read twice; `mergeAttempts` dedupes it). Throws on any page error (never
 *  half a history). */
export async function fetchFullAttemptHistoryForChildOrThrow(
  childId: string,
  pageSize: number = FULL_HISTORY_PAGE,
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<ChildAttempt[]> {
  const out: ChildAttempt[] = [];
  let total: number | null = null;
  for (let from = 0; ; ) {
    const { data, error, count } = await client
      .from(TABLE)
      .select("*", from === 0 ? { count: "exact" } : undefined)
      .eq("child_id", childId)
      .order("timestamp", { ascending: true })
      .order("event_key", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    if (from === 0 && typeof count === "number") total = count;
    const rows = (data ?? []) as Record<string, unknown>[];
    for (const r of rows) out.push(fromRow(r));
    if (rows.length === 0) break;
    from += rows.length;
    if (total !== null && from >= total) break;
  }
  return out;
}

/** MA-1202 (D-1103-10) — the ONE read every tier reader uses (the parent
 *  tree and its readers, the teacher class states, the parent overview, the
 *  badges): each skill's tier must come from that skill's own full history,
 *  never from a window shared across skills (the projection reads the
 *  cross-day review partner, reveal priming of the same item at any age, and
 *  the recent slice). Query 1 is the newest-HISTORY_DISCOVERY_WINDOW read;
 *  when it comes back short it IS the whole history (a fresh or light child:
 *  1 query). When it is full, ONE paged read of the full history follows
 *  (FULL_HISTORY_PAGE rows asked a page): 5,000 attempts cost 1 + 5 queries
 *  (1 + 10 under a 500-row server cap). Chronological; deduplicated with the
 *  window through `mergeAttempts`. Throws if any query fails. */
export async function fetchTierHistoryForChildOrThrow(
  childId: string,
  client: Pick<typeof supabase, "from"> = supabase,
  pageSize: number = FULL_HISTORY_PAGE,
): Promise<ChildAttempt[]> {
  const windowRows = await fetchAllAttemptsForChildOrThrow(childId, HISTORY_DISCOVERY_WINDOW, client);
  if (windowRows.length < HISTORY_DISCOVERY_WINDOW) return windowRows;
  const full = await fetchFullAttemptHistoryForChildOrThrow(childId, pageSize, client);
  return mergeAttempts(full, windowRows);
}

/** MA-1202 RED r1 S-1 — how long a read tier history is shared: the tree's
 *  staleTime. */
export const TIER_HISTORY_STALE_MS = 30_000;

/** A tier-history read for one child id (what every tier reader takes). */
export type TierHistoryRead = (childId: string) => Promise<ChildAttempt[]>;

/** The slice of a TanStack QueryClient the shared read needs. */
export interface TierHistoryCache {
  fetchQuery<T>(options: {
    queryKey: readonly unknown[];
    queryFn: () => Promise<T>;
    staleTime?: number;
  }): Promise<T>;
  getQueryState(queryKey: readonly unknown[]): { isInvalidated: boolean } | undefined;
}

/** MA-1202 RED r1 S-1 — ONE shared tier-history read per child per page: every
 *  reader (the tree and Where-now through useSkillGraph, MasteredOverTimeCard,
 *  the parent overview per union id, the badges, the teacher class states)
 *  reads through `qk.tierHistory.for(childId)` with `queryClient.fetchQuery`,
 *  so concurrent readers share one in-flight read and a reader within
 *  TIER_HISTORY_STALE_MS reuses it. The key sits under the tree's
 *  ["skill-graph"] prefix, so SessionRecap's invalidation reaches it. A reader
 *  whose OWN query was invalidated (`ownKey` state isInvalidated: recap's
 *  ["badge-sources"], Settings' parent-overview) reads fresh (staleTime 0) so
 *  a cached history never hides a just-finished session. Without a cache
 *  (tests, a caller outside React Query) it reads directly. A failed read
 *  rejects and is not cached. */
export function tierHistoryReader(
  cache: TierHistoryCache | null | undefined,
  ownKey?: readonly unknown[],
  client: Pick<typeof supabase, "from"> = supabase,
): TierHistoryRead {
  return (childId: string) => {
    if (!cache) return fetchTierHistoryForChildOrThrow(childId, client);
    const fresh = ownKey ? cache.getQueryState(ownKey)?.isInvalidated === true : false;
    return cache.fetchQuery({
      queryKey: qk.tierHistory.for(childId),
      queryFn: () => fetchTierHistoryForChildOrThrow(childId, client),
      staleTime: fresh ? 0 : TIER_HISTORY_STALE_MS,
    });
  };
}

/** Fetch all attempts whose itemId is in the given set, for the
 * parent-dashboard evidenceUsed join ("based on N attempts across M
 * sessions"). */
export async function fetchAttemptsByItemIds(
  childId: string,
  itemIds: string[],
): Promise<ChildAttempt[]> {
  if (itemIds.length === 0) return [];
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("child_id", childId)
    .in("item_id", itemIds);
  if (error) {
    console.error("[attemptLogger] fetchAttemptsByItemIds failed:", error);
    return [];
  }
  return (data ?? []).map(fromRow);
}

/** MA-559 slice c — the read-union fetch (design Piece 4, D3/REV 2 item 2:
 *  PER-ID windows, not a single `.in()` query). Runs one
 *  `fetchAllAttemptsForChildOrThrow` PER id (own `limit` each — the shadow's
 *  own 500-row budget can never be aged out by fresh family practice),
 *  concatenates, then stable-sorts by timestamp ascending. `Array.sort` is
 *  stable (ES2019+): for a single-id call this is a no-op re-sort of an
 *  already-sorted array — byte-identical to calling
 *  `fetchAllAttemptsForChildOrThrow` directly (MA-559 acceptance clause 2,
 *  "a claim with no shadow history behaves as today"). The client-injection
 *  seam mirrors `fetchAllAttemptsForChildOrThrow`'s own (:487) — every real
 *  caller relies on the default. */
export async function fetchAllAttemptsForChildrenOrThrow(
  childIds: string[],
  limit = 500,
  client: Pick<typeof supabase, "from"> = supabase,
): Promise<ChildAttempt[]> {
  const perChild = await Promise.all(
    childIds.map((id) => fetchAllAttemptsForChildOrThrow(id, limit, client)),
  );
  const merged = perChild.flat();
  merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  return merged;
}

/** Swallowing wrapper: returns [] on error, mirroring
 *  `fetchAllAttemptsForChild`'s own shape (:505) for the multi-id case. Not
 *  used by useOverviewData (which needs the throwing silent-failure
 *  contract, like every other Overview fetcher) — exists for API symmetry
 *  with every other engine read site in this file. */
export async function fetchAllAttemptsForChildren(
  childIds: string[],
  limit = 500,
): Promise<ChildAttempt[]> {
  try {
    return await fetchAllAttemptsForChildrenOrThrow(childIds, limit);
  } catch (error) {
    console.error("[attemptLogger] fetchAllAttemptsForChildren failed:", error);
    return [];
  }
}

// Test-only exports (camelCase ↔ snake_case round-trip coverage).
export const __test = { toRow, fromRow, attemptEventKey };
